import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAuth } from '../../context/AuthContext.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { PricingRulesEditor } from './components/PricingRulesEditor.js';
import type { PricingRule, PricingTemplateForm } from './types.js';

interface TemplateResponse extends Partial<PricingTemplateForm> {
  pricing_rules?: PricingRule[];
}

interface SupplierOption {
  _id: string;
  company_name: string;
  status?: 'active' | 'inactive' | 'suspended';
}

function rmToMinor(v: string): number | undefined {
  return v ? Math.round(Number(v) * 100) : undefined;
}
function minorToRm(v?: number): string {
  return v != null ? (v / 100).toFixed(2) : '';
}
function dateInput(v?: string | null): string {
  return v ? v.slice(0, 10) : '';
}

export function PricingTemplateFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/pricing');
  const isEdit = Boolean(id);
  // 362 (#1215): `/pricing/new?from=<id>` copies a rate card. It is still a CREATE: the source is only read, the
  // owner is chosen afresh, and the save is a POST. Same shape as the fleet's Copy (156).
  const [searchParams] = useSearchParams();
  const copyFromId = isEdit ? null : searchParams.get('from');
  const sourceId = id ?? copyFromId ?? undefined;

  const { data: template, isError: sourceFailed } = useQuery({
    queryKey: RESOURCE_KEYS.pricingTemplate.one(sourceId as string),
    queryFn: () => api.get<TemplateResponse>(`/pricing-templates/${sourceId}`),
    enabled: Boolean(sourceId),
  });
  const copyUnreadable = Boolean(copyFromId) && sourceFailed;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dayRm, setDayRm] = useState('');
  const [hourRm, setHourRm] = useState('');
  const [minMinutes, setMinMinutes] = useState('');
  const [minDays, setMinDays] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [rules, setRules] = useState<PricingRule[]>([]);

  /**
   * 061 (H10): which organisation this rate card belongs to.
   *
   * Offered ONLY to a caller who owns none — an administrator. A provider is never shown it: the
   * platform stamps their own organisation and ignores anything they send (FR-005/FR-010), so the
   * control would imply a power they do not have, and the options themselves come from
   * `GET /suppliers`, which demands `manage Supplier` and answers them 403.
   *
   * Offered on CREATE only. The owner is set once and never moves: cars are already priced by the
   * card, and 060 refuses a rate card owned by a different organisation than the car, so moving it
   * would strand every one of those cars without touching any of them (FR-006/FR-009).
   */
  const { activeSupplierId } = useAuth();
  const choosesSupplier = !activeSupplierId && !isEdit;
  const [supplierId, setSupplierId] = useState('');
  const [supplierError, setSupplierError] = useState('');

  // Only fetched when the caller actually has a choice to make.
  const { data: suppliers } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<{ items: SupplierOption[] }>('/suppliers'),
    enabled: choosesSupplier,
  });

  /**
   * Only organisations that may actually own a new rate card are offered.
   *
   * `GET /suppliers` returns every organisation that is not soft-deleted, `inactive` and `suspended`
   * ones included, but the platform accepts only an `active` owner — so offering the rest would let an
   * operator choose an option guaranteed to come back refused. Filtered here rather than in the query,
   * so the rate-card list (same query key) can still name the owner of a card whose organisation has
   * since been suspended.
   */
  const ownerOptions = suppliers?.items?.filter(
    (sup) => sup.status !== 'inactive' && sup.status !== 'suspended',
  );

  const currencySymbol = template?.currency_symbol ?? 'RM';

  useEffect(() => {
    if (!template) return;
    // A copy is told apart from its source in the list until it is renamed (decided 2026-10-06).
    setName(copyFromId ? `${template.name ?? ''} (copy)` : (template.name ?? ''));
    setDescription(template.description ?? '');
    setDayRm(minorToRm(template.base_price_per_day_minor));
    setHourRm(minorToRm(template.base_price_per_hour_minor));
    setMinMinutes(template.min_rental_minutes != null ? String(template.min_rental_minutes) : '');
    setMinDays(template.min_rental_days != null ? String(template.min_rental_days) : '');
    setValidFrom(dateInput(template.valid_from));
    setValidTo(dateInput(template.valid_to));
    setStatus(template.status ?? 'active');
    setRules(template.pricing_rules ?? []);
  }, [template, copyFromId]);

  // 161: one hook invalidates both the rate-card list and this rate card's own cache entry. Only
  // the list was invalidated before, so reopening this form inside the 30-second stale window
  // served pre-save values, and saving it wrote them back over the operator's edit.
  const mutation = useSaveMutation<Record<string, unknown>>({
    resource: 'pricingTemplate',
    id,
    mutationFn: (data) =>
      isEdit ? api.patch(`/pricing-templates/${id}`, data) : api.post('/pricing-templates', data),
    onSaved: () => navigate('/pricing'),
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // FR-011: refused here, before a request is spent, and stated inline rather than left to the
    // platform's 400 — the operator learns what is missing without a round trip (SC-004).
    if (choosesSupplier && !supplierId) {
      setSupplierError('Choose the supplier this rate card belongs to.');
      return;
    }
    void mutation.save({
      // Sent on create only, and only by a caller who has a choice: an edit must carry no owner at
      // all, because PATCH would otherwise move it (FR-006, FR-009).
      ...(choosesSupplier ? { supplier_id: supplierId } : {}),
      name,
      description: description || null,
      base_price_per_day_minor: rmToMinor(dayRm),
      base_price_per_hour_minor: rmToMinor(hourRm),
      min_rental_minutes: minMinutes ? Number(minMinutes) : null,
      min_rental_days: minDays ? Number(minDays) : null,
      valid_from: validFrom || null,
      valid_to: validTo || null,
      status,
      pricing_rules: rules,
    });
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Pricing Template' : 'Add Pricing Template'}
        description="Prices stored in sen (100 = RM 1.00). Bookings are 30-minute granular."
        fallback="/pricing"
      />
      {copyFromId && template && (
        <div data-testid="copying-from" className="mb-4 text-sm text-steel">
          Copying from{' '}
          <Link to={`/pricing/${copyFromId}/edit`} className="text-primary underline">
            {template.name}
          </Link>
          . Choose who the copy belongs to, and give it its own name.
        </div>
      )}
      {copyUnreadable && (
        <p className="mb-4 text-sm text-red-600">The rate card to copy could not be loaded.</p>
      )}
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Template</h2>
          <div className="space-y-4">
            <FormField label="Template Name" name="name" required>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
            {choosesSupplier && (
              <FormField label="Supplier" name="supplier_id" required error={supplierError}>
                <Select
                  id="supplier_id"
                  value={supplierId}
                  onChange={(e) => {
                    setSupplierId(e.target.value);
                    setSupplierError('');
                  }}
                >
                  <option value="">Select a supplier…</option>
                  {ownerOptions?.map((sup) => (
                    <option key={sup._id} value={sup._id}>
                      {sup.company_name}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <FormField label="Description" name="description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </FormField>
            <FormField label="Status" name="status">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Rental constraints</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Min rental minutes"
              name="min_rental_minutes"
              hint="Multiple of 30 (30-min granularity)"
            >
              <Input
                type="number"
                min="30"
                step="30"
                value={minMinutes}
                onChange={(e) => setMinMinutes(e.target.value)}
              />
            </FormField>
            <FormField label="Min rental days" name="min_rental_days">
              <Input
                type="number"
                min="1"
                value={minDays}
                onChange={(e) => setMinDays(e.target.value)}
              />
            </FormField>
            <FormField label="Valid from" name="valid_from">
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </FormField>
            <FormField label="Valid to" name="valid_to">
              <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Base rates</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Daily Rate (RM)" name="base_price_per_day_minor">
              <Input
                id="base_price_per_day_minor"
                type="number"
                step="0.01"
                min="0"
                value={dayRm}
                onChange={(e) => setDayRm(e.target.value)}
              />
            </FormField>
            <FormField label="Hourly Rate (RM)" name="base_price_per_hour_minor">
              <Input
                id="base_price_per_hour_minor"
                type="number"
                step="0.01"
                min="0"
                value={hourRm}
                onChange={(e) => setHourRm(e.target.value)}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <PricingRulesEditor rules={rules} currencySymbol={currencySymbol} onChange={setRules} />
        </Card>

        {mutation.error instanceof Error && (
          <p className="text-sm text-red-600">{mutation.error.message}</p>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={mutation.isPending} disabled={copyUnreadable}>
            {isEdit ? 'Save Changes' : 'Create Template'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
